/**
 * useSelectionHandlers - Event Handlers for Selection Management
 *
 * Single responsibility: Providing event handlers for user interactions.
 * Handles team selection, prop selection, tiebreaker input, clear, and quick pick.
 */

import { useCallback } from 'react';
import type { PickemDetailResponse } from '@bet99-pickem/api-types';
import { QUESTION_TYPES } from '@/constants/question-types';
import { getQuestionId, getOptionId } from '@/utils/id-helpers';
import type { SelectionState } from './useSelectionState';

/**
 * Creates event handlers for selection management
 *
 * @param setSelections - Function to update selections state
 * @param pickemDetails - Pickem details for quick pick logic
 * @returns Object with all handler functions
 */
export function useSelectionHandlers(
  setSelections: React.Dispatch<React.SetStateAction<SelectionState>>,
  pickemDetails: PickemDetailResponse | undefined
) {
  /**
   * Handle team or player selection
   */
  const handleTeamSelect = useCallback(
    (questionId: string, teamId: string | undefined) => {
      console.log('[Selection] Team/Player select:', { questionId, teamId });
      setSelections(prev => {
        if (teamId) {
          const newState = {
            ...prev,
            teams: { ...prev.teams, [questionId]: teamId },
          };
          console.log('[Selection] Team/Player selected - New state:', newState.teams);
          return newState;
        }

        // Remove selection if teamId is undefined
        const { [questionId]: _removed, ...rest } = prev.teams;
        const newState = {
          ...prev,
          teams: rest,
        };
        console.log('[Selection] Team/Player deselected - New state:', newState.teams);
        return newState;
      });
    },
    [setSelections]
  );

  /**
   * Handle prop question answer
   */
  const handlePropSelect = useCallback(
    (questionId: string, answer: 'yes' | 'no') => {
      console.log('[Selection] Prop select:', { questionId, answer });
      setSelections(prev => {
        const newState = {
          ...prev,
          props: { ...prev.props, [questionId]: answer },
        };
        console.log('[Selection] Prop selected - New state:', newState.props);
        return newState;
      });
    },
    [setSelections]
  );

  /**
   * Handle tiebreaker input change
   */
  const handleTieBreakerChange = useCallback(
    (value: string) => {
      console.log('[Selection] Tiebreaker change:', { value });
      setSelections(prev => {
        const newState = {
          ...prev,
          tieBreaker: value,
        };
        console.log('[Selection] Tiebreaker updated - New value:', newState.tieBreaker);
        return newState;
      });
    },
    [setSelections]
  );

  /**
   * Clear all selections
   */
  const clearSelections = useCallback(() => {
    console.log('[Selection] Clear All called - Clearing all selections');
    setSelections(prev => {
      console.log('[Selection] Clear All - Previous state:', prev);
      const newState = {
        teams: {},
        props: {},
        tieBreaker: '',
      };
      console.log('[Selection] Clear All - New state (should be empty):', newState);
      return newState;
    });
  }, [setSelections]);

  /**
   * Randomly select answers for all questions (Quick Pick)
   */
  const quickPick = useCallback(() => {
    if (!pickemDetails?.pickem.questions) return;

    const teamSelections: Record<string, string> = {};
    const propSelections: Record<string, 'yes' | 'no'> = {};

    pickemDetails.pickem.questions.forEach(question => {
      // Handle team and player questions
      if (
        question.question_type === QUESTION_TYPES.TEAM ||
        question.question_type === QUESTION_TYPES.PLAYER
      ) {
        const randomOption = question.options[Math.floor(Math.random() * question.options.length)];
        teamSelections[getQuestionId(question)] = getOptionId(randomOption);
      }
      // Handle prop questions
      else if (question.question_type === QUESTION_TYPES.PROP) {
        propSelections[getQuestionId(question)] = Math.random() > 0.5 ? 'yes' : 'no';
      }
    });

    // Generate random tiebreaker (40-80 range)
    const randomTieBreaker = Math.floor(Math.random() * 40 + 40).toString();

    setSelections({
      teams: teamSelections,
      props: propSelections,
      tieBreaker: randomTieBreaker,
    });
  }, [pickemDetails, setSelections]);

  return {
    handleTeamSelect,
    handlePropSelect,
    handleTieBreakerChange,
    clearSelections,
    quickPick,
  };
}
